import { useCallback } from 'react';
import { useCurrencyResolver } from '@/api/queries/certification-currency';
import { toNum, type DecimalString } from '@efacture/web-billing/lib/decimal';
import { formatMoney, formatNumber } from '@/lib/utils';

/**
 * Formats `amount` followed by the symbol of its currency: `code` when the
 * amount belongs to a document or a figure that states its currency, else
 * the certification currency of the day (an aggregate with no currency of
 * its own). With `centimes`, a whole amount prints its centimes too.
 */
export type MoneyFormatter = (amount: number | DecimalString, code?: string | null, centimes?: boolean) => string;

/**
 * A `MoneyFormatter` resolved against the certification-currency periods,
 * so a currency a period defines (ECO) prints the symbol the period states.
 * While the currency of the day is not known, or could not be read, an
 * amount without a code prints without a symbol, never with one it may not
 * be in; a code no table nor period words prints as the code itself.
 */
export function useMoneyFormatter(): MoneyFormatter {
  const { resolve } = useCurrencyResolver();
  return useCallback<MoneyFormatter>(
    (amount, code, centimes = false) => {
      const ref = resolve(code ?? null);
      if (ref !== null) return formatMoney(amount, ref, centimes);
      return code ? formatMoney(amount, code, centimes) : formatNumber(toNum(amount), centimes);
    },
    [resolve]
  );
}
